import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { JobService } from '../../../services/job.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { LanguageRoutingService } from '../../../services/language-routing.service';
import { Job } from '../../../models/job.model';
import { ToastService } from '../../../services/toast.service';
import { JobDetailCardComponent, JobDetailCardData } from '../../../components/job-detail-card/job-detail-card.component';
import { LoadMoreButtonComponent } from '../../../components/load-more-button/load-more-button.component';
import { getErrorTranslationKey } from '../../../../../../../libs/shared/src/lib/i18n/error-translations';

@Component({
  selector: 'app-saved-jobs',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    FormsModule,
    MatProgressSpinnerModule,
    RouterModule,
    JobDetailCardComponent,
    LoadMoreButtonComponent
  ],
  templateUrl: './saved-jobs.component.html',
  styleUrls: ['./saved-jobs.component.scss']
})
export class SavedJobsComponent implements OnInit {
  savedJobs: Job[] = [];
  isLoading = true;
  error: string | null = null;
  currentPage = 0;
  pageSize = 10;
  totalJobs = 0;
  hasMore = false;
  remainingCount = 0;
  loadingMore = false;
  unsavingJobId: number | null = null;
  limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 20, label: '20' },
    { value: 25, label: '25' },
    { value: 30, label: '30' }
  ];

  constructor(
    private jobService: JobService,
    private languageRoutingService: LanguageRoutingService,
    private toastService: ToastService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.loadSavedJobs();
  }

  loadSavedJobs(page: number = 0, append: boolean = false): void {
    if (append) {
      this.loadingMore = true;
    } else {
      this.isLoading = true;
      this.error = null;
    }

    this.jobService.getSavedJobs(page, this.pageSize).subscribe({
      next: (response) => {
        if (append) {
          this.savedJobs = [...this.savedJobs, ...response.jobs];
          this.loadingMore = false;
        } else {
          this.savedJobs = response.jobs;
          this.isLoading = false;
        }

        this.totalJobs = response.total;
        this.currentPage = page;
        this.updatePaginationState();
      },
      error: (err) => {
        console.error('Error loading saved jobs:', err);
        const titleKey = getErrorTranslationKey('LOAD_SAVED_JOBS_FAILED', 'title', 'jobOperations');
        const messageKey = getErrorTranslationKey('LOAD_SAVED_JOBS_FAILED', 'message', 'jobOperations');
        this.error = this.translocoService.translate(messageKey);
        this.toastService.error(
          this.translocoService.translate(titleKey),
          this.translocoService.translate(messageKey),
          true,
          5000
        );

        if (append) {
          this.loadingMore = false;
        } else {
          this.isLoading = false;
        }
      }
    });
  }

  loadMore(): void {
    if (!this.loadingMore && this.hasMore) {
      this.loadSavedJobs(this.currentPage + 1, true);
    }
  }

  unsaveJob(jobId: string): void {
    this.unsavingJobId = Number(jobId);

    this.jobService.unsaveJob(jobId).subscribe({
      next: (response) => {
        if (response.status === 200) {
          // Remove the job from the list
          this.savedJobs = this.savedJobs.filter(job => String(job.id) !== jobId);
          this.totalJobs--;

          // Update pagination state
          this.updatePaginationState();

          this.toastService.success(this.translocoService.translate('common.success'), this.translocoService.translate('jobs.toast.unsavedSuccess'), true, 3000);
        } else {
          const titleKey = getErrorTranslationKey('UNSAVE_JOB_FAILED', 'title', 'jobOperations');
          const messageKey = getErrorTranslationKey('UNSAVE_JOB_FAILED', 'message', 'jobOperations');
          this.toastService.error(
            this.translocoService.translate(titleKey),
            this.translocoService.translate(messageKey),
            true,
            5000
          );
        }
        this.unsavingJobId = null;
      },
      error: (err) => {
        console.error('Error unsaving job:', err);
        const titleKey = getErrorTranslationKey('UNSAVE_JOB_FAILED', 'title', 'jobOperations');
        const messageKey = getErrorTranslationKey('UNSAVE_JOB_FAILED', 'message', 'jobOperations');
        this.toastService.error(
          this.translocoService.translate(titleKey),
          this.translocoService.translate(messageKey),
          true,
          5000
        );
        this.unsavingJobId = null;
      }
    });
  }

  mapJobToCardData(job: Job): JobDetailCardData {
    return {
  id: job.id,
  jobTitle: job.title,
  tenantName: job.tenantName,
  tenantLogoUrl: job.tenantLogoUrl,
  location: job.location,
  jobType: job.type,
  salary: {
    // Force EUR display for SK locale; CurrencyDisplayPipe also coerces
    // when this string flows into a salary template.
    currency: '€',
    basicSalary: job.salary.basicSalary,
    period: job.salary.period || 'month'
  },
  postedAt: job.postedAt,
  jobCategory: job.jobCategory,
  jobPosition: job.jobPosition,
  experience: job.experience,
  isSaved: job.isSaved ? job.isSaved : false,
  matchScore: job.matchScore,
  jobDescription: '',
  jobCreatedAt: '',
  jobStatus: '',
  placeOfWork: [],
  tenantId: 0,
};
  }

  onJobUnsaved(job: Job): void {
    // Remove the job from the list when it's unsaved
    this.savedJobs = this.savedJobs.filter(savedJob => savedJob.id !== job.id);
    this.totalJobs--;

    // Update pagination state
    this.updatePaginationState();
  }

  trackByJobId(index: number, job: Job): string {
    return String(job.id);
  }

  getLocalizedRoute(path: string): string {
    return this.languageRoutingService.getLocalizedRoute(path);
  }

  onLimitChange(newLimit: number): void {
    this.pageSize = newLimit;
    this.currentPage = 0;
    this.loadSavedJobs();
  }

  private updatePaginationState(): void {
    const remaining = this.totalJobs - this.savedJobs.length;
    this.remainingCount = Math.min(remaining, this.pageSize);
    this.hasMore = this.savedJobs.length < this.totalJobs;
  }
}
